/**
 * useResearchSession — owns one research conversation.
 *
 * Responsibilities, and nothing else:
 *   • the `ResearchTurn[]` transcript (typed, not a Message string-bag)
 *   • opening a chat: newest page from SQL, no blob fetch on first paint
 *   • lazy blob hydration per turn, cached
 *   • older-page cursor pagination
 *   • sending a query, and mirroring the live run onto its assistant turn
 *   • the CLARIFY GATE's two halves: a send that comes back as a question
 *     rather than a run, and a question that was left unanswered being picked
 *     up again on open
 *
 * The live run itself belongs to `useResearchRun`; this hook consumes it.
 * Chat-group creation is deduped through one in-flight promise so an attach
 * and a send racing each other cannot make two groups.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAppDispatch } from '../../../store';
import {
  ResearchSource,
  researchCreateChatGroup,
  researchGetChatGroup,
  researchGetMessageContent,
  researchGetMessages,
  researchListChatGroups,
  researchAbandonPending,
  researchAnswerClarification,
  researchGetPendingPlan,
  researchSendQuery,
  researchSetSessionSettings,
  ResearchRating,
  ResearchRerunAccepted,
  researchRateMessage,
  researchRefineMessage,
  researchRerunMessage,
  researchStopProcessing,
  researchUpdateChatGroup,
  setChatGroupTitleLocally,
} from '../../../store/actions/ChatAction';
import { toResearchResponse } from '../adapter';
import { ResearchIntent } from '../types';
import { toSourceSelection } from '../sources';
import {
  AssistantTurn,
  HISTORY_PAGE_SIZE,
  ResearchTurn,
  blobMessageUuid,
  patchTurn,
  isAssistantTurn,
  isResumable,
  isAwaitingClarification,
  prependOlder,
  responseHasContent,
  toTurns,
} from '../turns';
import { TurnAttachment } from '../attachments';
import {
  PendingPlan,
  initialDraft,
  isClarifyResponse,
  AWAITING_CLARIFICATION,
  applyClarifyUpgrade,
  classifyAnswerResult,
  clarifyEventMatches,
  pendingFromClarifyEvent,
  pendingFromSendResult,
  resumedCorrelationId,
  sameQuestionSet,
  upgradedDraft,
  withPending,
} from '../clarify';
import {
  RERUN_MESSAGES,
  RerunResult,
  markVersionStatus,
  rerunFailureFor,
  versionFeedback,
  withNewVersion,
  withVersionFeedback,
} from '../versions';
import { useResearchRun } from '../useResearchRun';
import { useClarifyUpgrade } from '../useClarifyUpgrade';
import { CHAT_PAGE_SIZE, displayChatTitle, isPlaceholderTitle } from './chatList';

export interface ActiveRun {
  correlationId: string;
  messageUuid: string;
  assistantTurnId: string;
  chatUuid: string;
  explicitIntent: ResearchIntent | null;
  /**
   * Set only when the run is a rerun/edit/refine.
   *
   * A first answer has no version row of its own — the message row IS v1 — so
   * these stay undefined there and the reconcile keeps its old behaviour.
   */
  versionUuid?: string;
  versionNumber?: number;
}

export interface SendOptions {
  intent?: ResearchIntent | null;
  attachmentUuids?: string[];
  /**
   * The files themselves, for the turn the user is about to see.
   *
   * `attachmentUuids` is what the QUERY needs; this is what the TRANSCRIPT
   * needs — the chip has to appear on the sent message immediately, without
   * waiting for a reload to discover whether the backend echoes it back.
   */
  attachments?: TurnAttachment[];
  /**
   * A matter chosen BEFORE the chat existed.
   *
   * The relation is created with the chat row — the backend calls this "the
   * primary way" to connect a matter — after which every turn inherits it and
   * the client must not re-send it. Sent on the create AND on the first query
   * because either can be the request that first names the matter, and the
   * backend ignores the duplicate.
   */
  matterUuid?: string;
  /**
   * Output language chosen before the chat existed.
   *
   * Persisted as the session override (`output_language`) BEFORE the query is
   * queued, because the backend reads the language from the chat's stored
   * settings rather than from the query body — the same rule as sources.
   */
  outputLanguage?: string;
  /**
   * The user's current source selection.
   *
   * `POST /query` does NOT read sources from its body — the backend builds
   * `selected_sources` from the chat group's persisted `ResearchChatSource`
   * rows. So the selection has to be WRITTEN before the query is sent, and
   * on a brand-new chat there is no chat to write it to until `ensureChat`
   * has run. That is why this is flushed here, inside the send, rather than
   * left to the settings panel's own effect: the panel's effect bails out
   * when no chat exists, so a first message used to run with no sources at
   * all and the engine searched every jurisdiction.
   */
  sources?: ResearchSource[];
  /** Session jurisdiction override, flushed with the sources. */
  jurisdiction?: string | null;
}

/**
 * How long a run may go with no terminal pubsub event before the FE stops
 * waiting and asks the backend directly.
 *
 * The engine's publish to the user CAN fail — observed live as
 * `[PUBLISH_ERROR] … invalid status 'Not Found'` — after the answer was
 * finished and the blob uploaded. Waiting on an event that will never arrive
 * left the composer locked forever, so the event is treated as the fast path
 * and not as the only one.
 */
export const RECONCILE_AFTER_MS = 20_000;
/** How often to re-ask after that. */
export const RECONCILE_EVERY_MS = 15_000;

/**
 * What a turn's scope write would say.
 *
 * Used to skip a PUT that would change nothing: the common turn re-sends the
 * same selection it sent last time, and a round trip before every question
 * would be latency for no effect. Keyed by chat too, so the same selection in
 * a different chat is still written.
 */
export function scopeKey(chatUuid: string, patch: Record<string, any>): string {
  return JSON.stringify([chatUuid, patch]);
}

/**
 * Shown when the scope could not be persisted.
 *
 * The query reads its sources from the STORED rows, so sending anyway would
 * answer from whatever scope happened to be there — silently, and wrongly.
 * Blocking is the safe failure.
 */
export const SCOPE_WRITE_FAILED =
  'Could not apply your source selection, so the question was not sent. Please try again.';

/**
 * Idempotency key for one submission.
 *
 * Identifies WHAT was submitted, deliberately not which chat it landed in:
 * the first message creates the chat group, so the uuid changes between a
 * submit and its duplicate, and including it would make the key miss exactly
 * the most common case.
 *
 * The key only suppresses a repeat while the run it started is still live
 * (see `send`), so deliberately re-asking the same question after an answer
 * arrives is still allowed.
 */
export function submissionKey(text: string, options: SendOptions = {}): string {
  return [
    text.trim(),
    options.intent || 'auto',
    (options.attachmentUuids || []).join(','),
  ].join('|');
}

export interface UseResearchSessionResult {
  turns: ResearchTurn[];
  chatUuid: string;
  title: string;
  /** Rename the open chat; the stored title then sticks. */
  rename: (title: string) => Promise<void>;
  isLoadingHistory: boolean;
  isLoadingOlder: boolean;
  hasMoreHistory: boolean;
  isSending: boolean;
  /**
   * Increments each time a send persisted this chat's settings, so the
   * settings panel can re-read what the server now holds.
   */
  scopeWrites: number;
  /** The live run, or null when nothing is in flight. */
  run: ReturnType<typeof useResearchRun>;
  /**
   * Submit a question.
   *
   * Resolves TRUE only when the query was actually POSTed. False covers every
   * way a submit can end without reaching the engine — an empty box, the
   * duplicate guard, a blocked scope write, a failed POST — so the caller can
   * tell "sent" from "not sent" and, for instance, keep the user's staged
   * attachments instead of discarding them.
   */
  send: (text: string, options?: SendOptions) => Promise<boolean>;
  stop: () => Promise<void>;
  hydrate: (messageUuid: string) => void;
  /** Re-run an answer; `query` edits the question first. */
  rerun: (messageUuid: string, query?: string) => Promise<RerunResult>;
  /**
   * Refine an answer — improve this one rather than start over.
   *
   * `instruction` is optional; blank means "just improve it". Omitting
   * `fromVersion` refines whatever is current.
   */
  refine: (
    messageUuid: string,
    instruction?: string,
    fromVersion?: number
  ) => Promise<RerunResult>;
  /** Show a different version of an answer. */
  selectVersion: (messageUuid: string, version: number) => Promise<void>;
  /** Rate an answer up or down; the active thumb toggles off. */
  rate: (messageUuid: string, rating: ResearchRating) => Promise<void>;
  /**
   * CLARIFY GATE — answer an outstanding question, continuing the SAME turn.
   *
   * Resolving it enqueues under the correlation id and message uuid the turn
   * already has, so the run attaches to the turn already on screen rather than
   * appearing as a new one. An unresolved answer opens round two; there is no
   * round three.
   */
  answerClarification: (
    messageUuid: string,
    answers: Record<string, string>
  ) => Promise<void>;
  /**
   * Give up on an unanswered question.
   *
   * Distinct from `stop()`, which stops a RUN. Nothing is running here, and
   * routing this through the stop path leaves the pending question behind to
   * resurface on the next open.
   */
  abandonClarification: (messageUuid: string) => Promise<void>;
  loadOlder: () => void;
  open: (chatUuid: string) => Promise<void>;
  reset: () => void;
  /** Ensure a chat group exists (used before uploading attachments). */
  ensureChat: (titleSeed?: string, matterUuid?: string) => Promise<string>;
}

/**
 * The pending question on a turn, or an empty one.
 *
 * Never null: every caller wants a shape to read a field off, and a guard at
 * each site is how one of them ends up missing.
 */
function pendingOf(turns: ResearchTurn[], turnId: string): PendingPlan {
  const turn = turns.find((t) => t.id === turnId) as AssistantTurn | undefined;
  return (
    turn?.clarification?.pending || {
      status: AWAITING_CLARIFICATION,
      message_uuid: '',
      correlation_id: '',
      round: 1,
      max_rounds: 2,
      questions: [],
    }
  );
}

export function useResearchSession(
  routeChatUuid?: string,
  userId?: string | number | null
): UseResearchSessionResult {
  const dispatch = useAppDispatch();

  const [turns, setTurns] = useState<ResearchTurn[]>([]);
  const [chatUuid, setChatUuid] = useState(routeChatUuid || '');
  const [title, setTitle] = useState('');
  /** Read from the run's completion callback, which has no render closure. */
  const titleRef = useRef('');
  titleRef.current = title;
  /**
   * This chat has no name of its own yet — the surface is showing
   * "New Session" for it.
   *
   * Set when a chat is created and cleared by the first title the model
   * writes. Without it the "set the title once" rule treated whatever was on
   * screen as a name and froze it, which is how a truncated question ended up
   * in the breadcrumb and the sidebar for good.
   */
  const provisionalTitleRef = useRef(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [isLoadingOlder, setIsLoadingOlder] = useState(false);
  const [hasMoreHistory, setHasMoreHistory] = useState(false);
  const [historyCursor, setHistoryCursor] = useState<string | null>(null);
  const [isSending, setIsSending] = useState(false);
  const [activeRun, setActiveRun] = useState<ActiveRun | null>(null);
  /**
   * Bumped every time a send persists this chat's scope.
   *
   * The send is the only writer of the settings a NEW chat starts with — the
   * settings panel has nothing to PUT to before the chat exists — so it is
   * also the only thing that knows the server's copy just changed. The
   * surface watches this to re-read the settings, instead of leaving the
   * panel showing whatever the read that raced the write happened to see.
   */
  const [scopeWrites, setScopeWrites] = useState(0);

  // Mirrors state for the async paths, which must not read a stale closure.
  const chatUuidRef = useRef(chatUuid);
  chatUuidRef.current = chatUuid;
  const creationRef = useRef<Promise<string> | null>(null);
  const hydratedRef = useRef<Set<string>>(new Set());

  /**
   * Duplicate-submit guards. These are REFS, not state: a state flag is read
   * from a render closure, so two calls in the same tick both see `false` and
   * both POST. A ref is written synchronously, before the first await.
   */
  const inFlightRef = useRef(false);
  const lastKeyRef = useRef<string | null>(null);
  /** The correlation the UI is currently showing. Anything else is stale. */
  const activeCorrelationRef = useRef<string | null>(null);
  /** Chat currently being opened — guards a mid-run history reload. */
  const openedRef = useRef<string | null>(null);
  /** The active run, readable from callbacks without re-creating them. */
  const activeRunRef = useRef<ActiveRun | null>(null);
  /**
   * Turns whose rerun/refine POST is still open.
   *
   * The turn is put back in flight optimistically, BEFORE the request
   * resolves, so for that window it looks orphaned — and the resume sweep
   * would fetch the answer still stored against it and settle the turn as
   * finished with the OLD version. Held here until the run is attached.
   */
  const enqueueingRef = useRef<Set<string>>(new Set());
  /** The transcript, for timers that must see the latest turns. */
  const turnsRef = useRef<ResearchTurn[]>([]);

  activeRunRef.current = activeRun;
  turnsRef.current = turns;

  const run = useResearchRun(activeRun?.correlationId ?? null, {
    chatUuid: activeRun?.chatUuid,
    messageUuid: activeRun?.messageUuid,
    versionUuid: activeRun?.versionUuid,
    versionNumber: activeRun?.versionNumber,
    explicitIntent: activeRun?.explicitIntent ?? null,
    // A titled chat keeps its title; only the first turn names one.
    // A provisional seed is not a title: the first run must still name the chat.
    hasTitle: !!titleRef.current && !provisionalTitleRef.current,
    // Without this the hub cannot negotiate and never joins the user's group.
    userId,
  });

  /* ── chat group ─────────────────────────────────────────────── */

  const ensureChat = useCallback(async (
    // The first question, kept in the signature because callers have it and
    // the server may want it again — but it is NOT a title and is no longer
    // shown as one.
    _titleSeed?: string,
    matterUuid?: string
  ): Promise<string> => {
    if (chatUuidRef.current) return chatUuidRef.current;
    if (creationRef.current) return creationRef.current;

    /*
      NOTHING is shown as a title until the model writes one.

      The query used to be truncated into a provisional label so the sidebar
      row was not blank — but a truncated question is not a name, and neither
      is the placeholder the backend writes into the column when the row is
      created. Both were read as the chat's title by everyone who saw them.
      The surface renders "New Session" until a real title lands, so there is
      nothing to seed here.
    */

    const creation = (async () => {
      // `matter_uuid` is resolved server-side; a uuid that is not a matter in
      // this org 404s and the chat is NOT created, rather than quietly losing
      // the context the user asked for.
      const group = await dispatch(researchCreateChatGroup({
        ...(matterUuid ? { matter_uuid: matterUuid } : {}),
      }));
      chatUuidRef.current = group.uuid;
      // CLAIM IT AS OPEN. We are already in this chat and its transcript is
      // the one on screen. Without this, the route replace to
      // /research/<uuid> that follows makes the route effect call `open()`,
      // which wiped the just-sent turn and reloaded an empty history from
      // SQL — the "flicker that resets the UI and loses the results".
      openedRef.current = group.uuid;
      setChatUuid(group.uuid);
      setTitle(displayChatTitle(group.title));
      provisionalTitleRef.current = true;
      // The row exists NOW, so the history list is stale NOW. Every other
      // surface refetches its list after a create (delete already does, three
      // lines of this file away); research only refetched after the answer
      // landed, so a new chat was missing from the sidebar for the whole run
      // and appeared only if the completion event made it back.
      dispatch(researchListChatGroups(1, CHAT_PAGE_SIZE)).catch(() => {
        /* the sidebar keeps its loaded window; the next refetch corrects it */
      });
      return group.uuid;
    })();

    creationRef.current = creation;
    try {
      return await creation;
    } finally {
      creationRef.current = null;
    }
  }, [dispatch]);

  /* ── hydration ──────────────────────────────────────────────── */

  const hydrateRef = useRef(async (_chat: string, _message: string) => {});
  hydrateRef.current = async (chat: string, messageUuid: string) => {
    if (!chat || !messageUuid) return;
    const key = `${chat}:${messageUuid}`;
    if (hydratedRef.current.has(key)) return;
    hydratedRef.current.add(key);

    setTurns((prev) =>
      prev.map((t) =>
        t.kind === 'assistant' && t.messageUuid === messageUuid
          ? { ...t, hydration: 'loading' as const }
          : t
      )
    );

    try {
      const apiResponse = await dispatch(researchGetMessageContent(chat, messageUuid));
      const blobData = (apiResponse as any)?.content ?? apiResponse;

      /**
       * IDENTITY CHECK.
       *
       * The blob is fetched per message uuid, so it should always be this
       * turn's — but if the stored content ever names a different message,
       * rendering it here would put one turn's answer under another's
       * question. Refusing is the safe failure: the turn keeps its own SQL
       * summary instead of showing someone else's answer.
       */
      const declared = blobMessageUuid(blobData);
      if (declared && declared !== messageUuid) {
        hydratedRef.current.delete(key);
        setTurns((prev) =>
          prev.map((t) =>
            t.kind === 'assistant' && t.messageUuid === messageUuid
              ? { ...t, hydration: 'failed' as const }
              : t
          )
        );
        return;
      }

      setTurns((prev) =>
        prev.map((t) => {
          if (t.kind !== 'assistant' || t.messageUuid !== messageUuid) return t;
          const response = toResearchResponse(blobData, {
            messageUuid,
            fallbackMarkdown: t.summary,
          });
          return {
            ...t,
            response,
            intent: response.intent,
            verification: response.meta.verification,
            hydration: 'hydrated' as const,
            // A hydrated turn is finished by definition. Any half-set live
            // object left on it could only read as "still running".
            live: undefined,
          };
        })
      );
    } catch {
      // Leave the SQL summary on screen — it is still a readable answer.
      hydratedRef.current.delete(key);
      setTurns((prev) =>
        prev.map((t) =>
          t.kind === 'assistant' && t.messageUuid === messageUuid
            ? { ...t, hydration: 'failed' as const }
            : t
        )
      );
    }
  };

  const hydrate = useCallback((messageUuid: string) => {
    hydrateRef.current(chatUuidRef.current, messageUuid);
  }, []);

  /* ── open a chat ────────────────────────────────────────────── */

  const open = useCallback(async (uuid: string) => {
    if (!uuid) return;
    // Already showing this chat: a re-invoke (StrictMode, a route replace we
    // ourselves triggered) must not wipe the transcript.
    if (openedRef.current === uuid) return;
    // A run is streaming into the current transcript. Reloading history now
    // would drop the answer the user is waiting for; the route settles to
    // this same chat anyway.
    if (activeCorrelationRef.current && uuid === chatUuidRef.current) return;
    openedRef.current = uuid;
    setIsLoadingHistory(true);
    setTurns([]);
    setHistoryCursor(null);
    setHasMoreHistory(false);
    hydratedRef.current.clear();
    chatUuidRef.current = uuid;
    setChatUuid(uuid);

    try {
      const [group, page, pendingResult] = await Promise.all([
        dispatch(researchGetChatGroup(uuid)),
        // Newest page only. Each row carries `summary` + `intent`, so the
        // whole page paints from SQL — no blob fetch per turn on first paint.
        dispatch(researchGetMessages(uuid, { limit: HISTORY_PAGE_SIZE })),
        /**
         * RESUME. A chat may have been left with a question outstanding —
         * possibly weeks ago — and it is answerable exactly where it was
         * asked. Fetched HERE, in the same round trip as the history, because
         * it is part of what the chat IS rather than an afterthought: a
         * transcript that paints and then grows a question a beat later reads
         * as something that just happened, which it did not.
         *
         * It never throws (see the thunk): a chat that loads without its
         * pending card is degraded, one that refuses to load is broken.
         */
        dispatch(researchGetPendingPlan(uuid)),
      ]);

      const loaded = toTurns(page?.items || []);

      /**
       * ATOMIC FIRST PAINT.
       *
       * Every answer on the opening page is fetched BEFORE anything is
       * shown, so a reopened chat appears complete — question and answer
       * together — rather than a column of questions with answers popping in
       * underneath. The fetches run in parallel, so the wait is one round
       * trip rather than N, and older pages still hydrate lazily on scroll.
       *
       * A blob that fails or names another message leaves its turn on the
       * SQL summary; one bad blob never holds up the rest.
       */
      const hydrated = await Promise.all(
        loaded.map(async (turn) => {
          if (turn.kind !== 'assistant' || !turn.messageUuid) return turn;
          /**
           * A run that is STILL GOING has no blob to fetch.
           *
           * This was the live "No relevant documents were found" after a hard
           * refresh: the fetch returned nothing, `toResearchResponse` made an
           * empty envelope, and the turn was written as hydrated with
           * `live: undefined` — a finished answer containing nothing. It is
           * left in flight instead, for the resume sweep to settle.
           */
          if (isResumable(turn)) return turn;
          /**
           * An OPEN QUESTION has no blob and never will until it is answered.
           * Same failure as the running turn above: the fetch returns nothing,
           * the adapter makes an empty envelope, and the turn is written as a
           * finished answer containing nothing — which renders as "no relevant
           * documents were found" for a question that was never asked of the
           * corpus at all.
           */
          if (turn.awaitingClarification) return turn;
          const key = `${uuid}:${turn.messageUuid}`;
          try {
            const raw = await dispatch(researchGetMessageContent(uuid, turn.messageUuid));
            const blob = (raw as any)?.content ?? raw;
            const declared = blobMessageUuid(blob);
            if (declared && declared !== turn.messageUuid) return turn;

            const response = toResearchResponse(blob, {
              messageUuid: turn.messageUuid,
              fallbackMarkdown: turn.summary,
            });
            // An empty envelope is not an answer. Keep the SQL summary rather
            // than retiring the turn as answered-with-nothing.
            if (!responseHasContent(response) && !turn.summary) return turn;
            hydratedRef.current.add(key);
            return {
              ...turn,
              response,
              intent: response.intent,
              verification: response.meta.verification,
              hydration: 'hydrated' as const,
              live: undefined,
            };
          } catch {
            return turn;
          }
        })
      );
      setTurns(withPending(hydrated, (pendingResult as any)?.pending || null));
      // A stored title is real — it was written by the completion reconcile
      // or by the user.
      setTitle(displayChatTitle(group?.title));
      provisionalTitleRef.current = false;
      setHistoryCursor(page?.next_cursor ?? null);
      setHasMoreHistory(!!page?.has_more);

    } catch {
      setTurns([]);
      setTitle('');
    } finally {
      setIsLoadingHistory(false);
    }
  }, [dispatch]);

  /**
   * `reset` in a ref, so the route effect below can reach it.
   *
   * It is declared far further down (it clears state every path here owns),
   * and the effect only ever runs after render, so the ref is populated by
   * the time it is read.
   */
  const resetRef = useRef(() => {});

  /**
   * THE ROUTE'S UUID IS AUTHORITATIVE — in both directions.
   *
   * It gains a uuid on mount and whenever the user switches chats; `open()`
   * no-ops when that uuid is already showing. It LOSES one when the user goes
   * back to /research from the breadcrumb or the back caret, and that is a
   * request for a fresh session, not a cosmetic URL change: without the reset
   * the old transcript, title and run state stayed on screen under a URL that
   * no longer named them.
   *
   * The guard is `previous`, not merely "no uuid": a chat created by sending
   * the first message also has no uuid in the route for a beat, and resetting
   * there would wipe the query the user just sent.
   */
  const previousRouteChatRef = useRef(routeChatUuid);
  useEffect(() => {
    const previous = previousRouteChatRef.current;
    previousRouteChatRef.current = routeChatUuid;
    if (routeChatUuid) {
      open(routeChatUuid);
      return;
    }
    if (previous) resetRef.current();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeChatUuid]);

  /* ── older pages ────────────────────────────────────────────── */

  const loadOlderRef = useRef(async () => {});
  loadOlderRef.current = async () => {
    const uuid = chatUuidRef.current;
    if (!uuid || !historyCursor || !hasMoreHistory || isLoadingOlder) return;
    setIsLoadingOlder(true);
    try {
      const page = await dispatch(researchGetMessages(uuid, {
        before: historyCursor,
        limit: HISTORY_PAGE_SIZE,
      }));
      setTurns((prev) => prependOlder(toTurns(page?.items || []), prev));
      setHistoryCursor(page?.next_cursor ?? null);
      setHasMoreHistory(!!page?.has_more);
    } catch {
      // Keep what is on screen; the sentinel stays and can be retried.
    } finally {
      setIsLoadingOlder(false);
    }
  };
  const loadOlder = useCallback(() => { loadOlderRef.current(); }, []);

  /* ── sending ────────────────────────────────────────────────── */

  /** The scope we last wrote successfully — see the guard inside `send`. */
  const persistedScopeRef = useRef<string | null>(null);

  const sendRef = useRef(async (_text: string, _options?: SendOptions) => false);
  sendRef.current = async (text: string, options: SendOptions = {}): Promise<boolean> => {
    const trimmed = text.trim();
    if (!trimmed) return false;

    // EXACTLY ONE POST PER SUBMIT.
    //
    // The mutex is synchronous, so a second call in the same tick — a
    // double-fired handler, a StrictMode re-invoke, a stray Enter while the
    // first request is still open — returns before reaching the network.
    // The key additionally collapses a repeat of the SAME submission, which
    // is what produced the duplicate runs seen in the engine logs (identical
    // query text, two correlation ids, the second hitting the intent cache).
    const key = submissionKey(trimmed, options);
    if (inFlightRef.current) return false;
    if (lastKeyRef.current === key && activeCorrelationRef.current) return false;

    inFlightRef.current = true;
    lastKeyRef.current = key;
    setIsSending(true);

    const localId = `local-${Date.now()}`;
    setTurns((prev) => [
      ...prev,
      {
        kind: 'user',
        id: `${localId}_user`,
        text: trimmed,
        createdAt: new Date(),
        ...(options.attachments?.length ? { attachments: options.attachments } : {}),
      },
      {
        kind: 'assistant',
        id: `${localId}_assistant`,
        createdAt: new Date(),
        intent: options.intent ?? null,
        hydration: 'hydrated',
        live: { stages: [], done: false, error: null },
        // A run is being opened here, so this turn's answer arrives while the
        // user watches. `live` cannot carry that: every settle path clears
        // it. See `AssistantTurn.liveArrival`.
        liveArrival: true,
      },
    ]);

    try {
      // A chat that does not exist yet is created WITH the matter, so the
      // relation exists before the first question is queued.
      const isNewChat = !chatUuidRef.current;
      const uuid = await ensureChat(trimmed, options.matterUuid);

      /**
       * Persist the selection FIRST — the query reads its sources from the
       * chat group's rows, not from its own body.
       *
       * An EMPTY selection is never written. `[]` is a truthy array, so the
       * old check sent `sources: []` whenever the local selection happened to
       * be empty — which on a follow-up (before the settings adopt lands, or
       * with the panel never opened) WIPED the chat's stored scope and sent
       * retrieval across every jurisdiction. Empty means "keep what the chat
       * has", which is also the endpoint's own contract: it can never clear a
       * scope.
       */
      const sourcesToWrite = options.sources?.length
        ? toSourceSelection(options.sources)
        : null;
      const patch = {
        ...(sourcesToWrite ? { sources: sourcesToWrite } : {}),
        ...(options.jurisdiction !== undefined ? { jurisdiction: options.jurisdiction } : {}),
        ...(options.outputLanguage ? { output_language: options.outputLanguage } : {}),
      };

      /**
       * THE SCOPE GUARD.
       *
       * The write must have COMPLETED before the query goes out, and a write
       * that failed must stop the query rather than let it run against
       * whatever scope the backend still holds. That failure mode produces a
       * confident answer drawn from the wrong sources, with nothing on screen
       * to suggest anything went wrong — the worst outcome available here.
       *
       * The write is skipped entirely when it would restate what was already
       * persisted, so the common turn costs no extra round trip.
       */
      if (Object.keys(patch).length > 0) {
        const key = scopeKey(uuid, patch);
        if (key !== persistedScopeRef.current) {
          try {
            await dispatch(researchSetSessionSettings(uuid, patch));
            persistedScopeRef.current = key;
            setScopeWrites((n) => n + 1);
          } catch {
            // Unknown now, so the next attempt must write again rather than
            // trust a key from before the failure.
            persistedScopeRef.current = null;
            lastKeyRef.current = null;
            setTurns((prev) =>
              patchTurn(prev, `${localId}_assistant`, (turn) => ({
                ...turn,
                live: { stages: [], done: true, error: SCOPE_WRITE_FAILED },
              }))
            );
            return false;
          }
        }
      }

      const result = await dispatch(researchSendQuery(uuid, trimmed, undefined, {
        ...(options.attachmentUuids?.length ? { attachmentUuids: options.attachmentUuids } : {}),
        // Only on the first query of a chat that had no matter — a later turn
        // inherits the link and re-sending it is explicitly ignored upstream.
        ...(isNewChat && options.matterUuid ? { matterUuid: options.matterUuid } : {}),
        ...(options.intent ? { intent: options.intent } : {}),
      }));

      const { correlation_id, message_uuid } = result;
      const assistantTurnId = `${message_uuid}_assistant`;

      /**
       * THE GATE ASKED.
       *
       * Nothing was enqueued and nothing was metered — the ~2-minute all-live
       * fan-out this replaces has not started and will not until the question
       * is answered. So the optimistic assistant turn stops being an
       * in-flight run and becomes the question, IN PLACE: same turn id, same
       * position in the transcript, no spinner. The run subscription is never
       * opened, because there is no run.
       */
      if (isClarifyResponse(result)) {
        const pending = pendingFromSendResult(result);
        setTurns((prev) =>
          prev.map((t) => {
            if (t.id === `${localId}_user`) {
              return { ...t, id: `${message_uuid}_user`, messageUuid: message_uuid };
            }
            if (t.id === `${localId}_assistant`) {
              return {
                ...t,
                id: assistantTurnId,
                messageUuid: message_uuid,
                live: undefined,
                awaitingClarification: true,
                clarification: { pending, draft: initialDraft(pending) },
              };
            }
            return t;
          })
        );
        // A question is not a run, so the submission key is released: the user
        // may legitimately re-ask the same thing rather than answering.
        lastKeyRef.current = null;
        return true;
      }

      // The label the server holds for this chat is NOT adopted here. On a
      // first query it is the placeholder it just derived — "New Research" —
      // and showing it is indistinguishable from showing nothing, except that
      // it then has to be un-shown. The surface says "New Session" until the
      // model's own title arrives on the completion event.
      if (result.chat_title && !isPlaceholderTitle(result.chat_title)) {
        setTitle((current) => (current && !provisionalTitleRef.current ? current : result.chat_title!));
      }

      // Re-key the optimistic turns onto the real message uuid.
      setTurns((prev) =>
        prev.map((t) => {
          if (t.id === `${localId}_user`) {
            return { ...t, id: `${message_uuid}_user`, messageUuid: message_uuid };
          }
          if (t.id === `${localId}_assistant`) {
            return { ...t, id: assistantTurnId, messageUuid: message_uuid };
          }
          return t;
        })
      );

      // Supersession: this correlation is now THE run. A completion for any
      // earlier one is stale and must never render over it.
      activeCorrelationRef.current = correlation_id;
      setActiveRun({
        correlationId: correlation_id,
        messageUuid: message_uuid,
        assistantTurnId,
        chatUuid: uuid,
        explicitIntent: options.intent ?? null,
      });
      return true;
    } catch {
      // A failed submit must be retryable — release the key, not just the mutex.
      lastKeyRef.current = null;
      setTurns((prev) =>
        patchTurn(prev, `${localId}_assistant`, (turn) => ({
          ...turn,
          live: { stages: [], done: true, error: 'Failed to send. Please try again.' },
        }))
      );
      return false;
    } finally {
      inFlightRef.current = false;
      setIsSending(false);
    }
  };
  const send = useCallback(
    (text: string, options?: SendOptions): Promise<boolean> => sendRef.current(text, options),
    []
  );

  const stop = useCallback(async () => {
    const uuid = chatUuidRef.current;
    const stopping = activeRunRef.current;

    /**
     * SETTLE FIRST, THEN DETACH.
     *
     * Detaching alone was the bug: it nulled the correlation and the active
     * run, which made the mirror effect below return early forever — so the
     * turn kept `live.done === false`, `isStreaming` stayed true, and the
     * composer stayed locked. Stop could never unlock it, whatever the
     * backend answered. The turn is finished here, synchronously, before
     * anything else can go wrong.
     */
    if (stopping) {
      setTurns((prev) =>
        patchTurn(prev, stopping.assistantTurnId, (turn): AssistantTurn => ({
          ...turn,
          live: { stages: turn.live?.stages || [], done: true, error: turn.live?.error ?? null },
        }))
      );
    }

    activeCorrelationRef.current = null;
    lastKeyRef.current = null;
    inFlightRef.current = false;
    setIsSending(false);
    setActiveRun(null);

    if (uuid) {
      try {
        await dispatch(researchStopProcessing(uuid));
      } catch {
        // Best effort, and deliberately AFTER the reset: the UI must return
        // to idle whether or not the backend agrees it stopped.
      }
    }
  }, [dispatch]);

  /* ── reconciliation: completion without a terminal event ────── */

  /**
   * Ask the backend whether the run already finished.
   *
   * `/messages/{uuid}/content` returning a full response IS completion — the
   * blob only exists once the engine wrote it. Adopting it settles the turn
   * exactly as a terminal event would: the typed answer renders and `live` is
   * cleared, which is what unlocks the composer.
   *
   * Returns true only when real content came back. A blob that is missing or
   * still empty leaves the run alone to keep waiting.
   */
  const settleFromContentRef = useRef(
    async (_chat: string, _messageUuid: string, _turnId: string) => false
  );
  settleFromContentRef.current = async (chat, messageUuid, turnId) => {
    if (!chat || !messageUuid) return false;
    try {
      const apiResponse = await dispatch(researchGetMessageContent(chat, messageUuid));
      const blobData = (apiResponse as any)?.content ?? apiResponse;

      // The same identity guard hydration uses: one turn must never render
      // another turn's answer.
      const declared = blobMessageUuid(blobData);
      if (declared && declared !== messageUuid) return false;

      const response = toResearchResponse(blobData, { messageUuid });
      if (!responseHasContent(response)) return false;

      hydratedRef.current.add(`${chat}:${messageUuid}`);
      setTurns((prev) =>
        patchTurn(prev, turnId, (turn): AssistantTurn => ({
          ...turn,
          response,
          intent: response.intent,
          verification: response.meta.verification,
          hydration: 'hydrated' as const,
          // Finished by definition — anything left on `live` could only read
          // as "still running".
          live: undefined,
        }))
      );
      return true;
    } catch {
      return false;
    }
  };

  /**
   * The safety net for a terminal event that never arrives.
   *
   * Starts only after a grace period, so the normal case — the event lands in
   * a second or two — costs nothing. Once the answer is adopted the run is
   * detached, releasing the composer.
   */
  useEffect(() => {
    if (!activeRun) return;
    if (run.done || run.response) return;

    let cancelled = false;
    const attempt = async () => {
      const current = activeRunRef.current;
      if (cancelled || !current) return;
      /* A turn that has come back as a QUESTION has no blob to find. The
         engine holds before retrieval, so `/content` answers NOT_FOUND for as
         long as the card is open — and polling it on a loop is the "it just
         failed" the user sees. The card is the state; there is nothing to
         reconcile until they answer. */
      const held = turnsRef.current.find(
        (t) => t.id === current.assistantTurnId
      );
      if (held && isAwaitingClarification(held)) return;
      const settled = await settleFromContentRef.current(
        current.chatUuid,
        current.messageUuid,
        current.assistantTurnId
      );
      if (cancelled || !settled) return;
      // The run is over; stop listening for an event that is not coming.
      activeCorrelationRef.current = null;
      lastKeyRef.current = null;
      inFlightRef.current = false;
      setIsSending(false);
      setActiveRun(null);
    };

    const first = setTimeout(() => {
      attempt();
      interval = setInterval(attempt, RECONCILE_EVERY_MS);
    }, RECONCILE_AFTER_MS);
    let interval: ReturnType<typeof setInterval> | undefined;

    return () => {
      cancelled = true;
      clearTimeout(first);
      if (interval) clearInterval(interval);
    };
  }, [activeRun, run.done, run.response]);

  /**
   * RESUME A RUN THIS CLIENT NEVER STARTED.
   *
   * After a hard refresh there is no `activeRun` and no correlation id, so
   * the pubsub path cannot help even when the socket is healthy: the events
   * for that run were addressed to a page that no longer exists. The turn is
   * reopened as in-flight from its stored status (see `toTurn`), and the only
   * way it can finish is by asking the backend.
   *
   * Polls from the first tick rather than after a grace period — the run has
   * already been going for however long the page took to reload, and it may
   * well be finished before the transcript finishes painting.
   */
  /**
   * A turn nobody is watching: still running, and NOT the run this client
   * started. The active run has its own reconciler with a grace period,
   * because for it the pubsub event really is the fast path.
   */
  const orphaned = useCallback(
    (list: ResearchTurn[]): AssistantTurn[] =>
      list.filter(
        (t) =>
          isResumable(t) &&
          t.id !== activeRunRef.current?.assistantTurnId &&
          !enqueueingRef.current.has(t.id)
      ) as AssistantTurn[],
    []
  );

  useEffect(() => {
    if (orphaned(turns).length === 0) return;

    let cancelled = false;
    const sweep = async () => {
      const chat = chatUuidRef.current;
      if (cancelled || !chat) return;
      // Snapshot: a turn settled by an earlier pass is gone from the list.
      const pending = orphaned(turnsRef.current);
      for (const turn of pending) {
        if (cancelled) return;
        // eslint-disable-next-line no-await-in-loop
        await settleFromContentRef.current(chat, turn.messageUuid || '', turn.id);
      }
    };

    sweep();
    const interval = setInterval(sweep, RECONCILE_EVERY_MS);
    return () => { cancelled = true; clearInterval(interval); };
    // Keyed on WHICH turns are resuming, not on the array identity: every
    // interim patch makes a new array and would restart the timer.
  }, [orphaned(turns).map((t) => t.id).join(',')]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── mirror the live run onto its assistant turn ────────────── */

  useEffect(() => {
    if (!activeRun) return;
    // Supersession: a completion arriving for a correlation the user has
    // already moved past must not overwrite the newer turn.
    if (activeCorrelationRef.current !== activeRun.correlationId) return;
    /**
     * TURN ISOLATION.
     *
     * The run hook resets in an effect, so for exactly one render after a new
     * run starts it still returns the PREVIOUS run's state. Copying that onto
     * the new turn is how a follow-up rendered the earlier turn's document —
     * same lead-in, same card, counted as "v2". State that does not name this
     * run is not this run's state.
     */
    if (run.correlationId !== activeRun.correlationId) return;
    setTurns((prev) =>
      patchTurn(prev, activeRun.assistantTurnId, (turn): AssistantTurn => ({
        ...turn,
        intent: run.intent ?? turn.intent,
        // Live lines now; once the response lands its persisted copy wins,
        // so a reload reads identically. Emptiness never overwrites — see the
        // note on `stages` below.
        interim: run.interim.length
          ? run.interim.map((m) => m.text)
          : (turn.interim ?? []),
        // The working indicator's vocabulary for this question. Empty until
        // the engine sends it; never invented here.
        cues: run.cues?.length ? run.cues : turn.cues,
        // A response IS the completion. Writing `done` from the run flag
        // alone left the LAST turn of a chat spinning whenever the final
        // update did not land (a dropped `completed`, or the mirror stopping
        // first) — and the last turn is the one nothing later comes to
        // correct, which is why it was always the stuck one.
        live: {
          /*
            THE STREAM MUST NOT BLINK OUT.

            `useResearchRun` resets when its correlation changes — and a
            clarify resume re-subscribes, so for a beat it returns an empty
            run for a turn that has minutes of narration behind it. Mirroring
            that emptiness wiped the progress lines and the stage list off the
            screen, and the next event put them back: the flicker the user saw
            as "we lost all the progress messages".

            An empty run says nothing about the turn, so it overwrites
            nothing. Real content replaces; emptiness leaves what is there —
            the same rule `cues` below has always had.
          */
          stages: run.stages.length ? run.stages : (turn.live?.stages ?? []),
          done: run.done || !!run.response,
          error: run.error,
        },
        ...(run.response
          ? {
              response: run.response,
              verification: run.verification ?? undefined,
              hydration: 'hydrated' as const,
              // The version this run produced is finished; the chip should
              // not keep reading as in-progress.
              versions: turn.activeVersion
                ? markVersionStatus(turn.versions, turn.activeVersion, 'completed')
                : turn.versions,
            }
          : {}),
      }))
    );
  }, [run.correlationId, run.stages, run.done, run.error, run.response, run.intent,
      run.verification, run.interim, run.cues, activeRun]);

  // The generated title lands on the run; adopt it ONLY to name a chat that
  // has none. An existing title — generated on turn one, or typed by the
  // user — is never overwritten by a later turn.
  useEffect(() => {
    if (!run.chatTitle) return;
    // Read the flag NOW, not inside the updater: React runs an updater
    // lazily, by which time the line below has already cleared the ref — so
    // the generated title was compared against a stale `false` and thrown
    // away, leaving the provisional query text on screen.
    const wasProvisional = provisionalTitleRef.current;
    provisionalTitleRef.current = false;
    const generated = displayChatTitle(run.chatTitle);
    if (!generated) return;
    setTitle((current) => (current && !wasProvisional ? current : generated));
    // The sidebar row is renamed in the SAME moment, from the same value —
    // no refetch in between, so the header and the list never disagree.
    const uuid = chatUuidRef.current;
    if (uuid) dispatch(setChatGroupTitleLocally(uuid, generated));
  }, [run.chatTitle, dispatch]);

  /** Rename the chat. The user's title wins over anything generated. */
  const rename = useCallback(async (next: string) => {
    const uuid = chatUuidRef.current;
    const clean = next.trim();
    if (!uuid || !clean || clean === titleRef.current) return;
    setTitle(clean);
    // A typed name is final — nothing generated may overwrite it.
    provisionalTitleRef.current = false;
    dispatch(setChatGroupTitleLocally(uuid, clean));
    await dispatch(researchUpdateChatGroup(uuid, { title: clean }));
    dispatch(researchListChatGroups());
  }, [dispatch]);

  // Once the run finishes, the submission is no longer "in progress": the same
  // question may be asked again deliberately. Only the live-run window is
  // deduped.
  useEffect(() => {
    if (run.done) lastKeyRef.current = null;
  }, [run.done]);

  /* ── rerun and versions ─────────────────────────────────────── */

  /**
   * Re-run an answer.
   *
   * The new run belongs to the SAME turn, so the turn is put back in flight
   * in place rather than a new exchange being appended: the resume sweep and
   * the live channel both key off the message, and both already know how to
   * finish a turn that is running.
   */
  /**
   * The ONE path that puts a turn back in flight for a new version.
   *
   * Re-run, edit-and-run and refine differ only in which request they send;
   * everything after that — the optimistic in-flight state, adopting the new
   * version number, and the failure card on rejection — is identical, and
   * having three copies of it is how they would drift apart.
   */
  const enqueueVersion = useCallback(async (
    messageUuid: string,
    request: (chat: string) => Promise<ResearchRerunAccepted>,
    /** The question the new version answers, when the caller changed it. */
    query?: string
  ): Promise<RerunResult> => {
    const chat = chatUuidRef.current;
    if (!chat || !messageUuid) return { ok: false, reason: 'failed' };

    const turnId = `${messageUuid}_assistant`;
    // Optimistic: the answer is being rewritten, so stop showing the old one
    // as final. The previous version is not lost — it is v(n-1).
    enqueueingRef.current.add(turnId);
    setTurns((prev) =>
      patchTurn(prev, turnId, (turn): AssistantTurn => ({
        ...turn,
        rerunError: undefined,
        live: { stages: [], done: false, error: null },
        // The third and last place a run is opened. A re-run PRODUCES an
        // answer, so the user watches it arrive exactly as they would a
        // first one — unlike switching to a version that already exists,
        // which is a fetch and stays flat.
        liveArrival: true,
      }))
    );

    try {
      const accepted = await request(chat);

      /**
       * ATTACH THE LIVE RUN. This is the whole point.
       *
       * A rerun/edit/refine is queued under a NEW correlation id — the
       * backend mints one per version — and `useResearchRun` subscribes per
       * correlation id. Accepting the 202 without setting the active run left
       * nothing listening on the hub: every `research.*` event for the new
       * version was published, filtered out by the correlation guard (or with
       * no listener attached at all), and the turn sat on the optimistic
       * `live` state until the 20s reconcile poll or a completion
       * notification eventually fetched the finished answer. That is exactly
       * the reported "stuck loading, then suddenly done".
       *
       * Written to the ref FIRST, synchronously: the mirror effect and the
       * resume sweep both read it, and both run before React commits the
       * state below.
       */
      const nextRun: ActiveRun = {
        correlationId: accepted.correlation_id,
        messageUuid,
        assistantTurnId: turnId,
        chatUuid: chat,
        // The engine re-derives the intent for the new version from the
        // stored turn; claiming an explicit one here would mislabel it.
        explicitIntent: null,
        versionUuid: accepted.version_uuid,
        versionNumber: accepted.version_number,
      };
      activeCorrelationRef.current = accepted.correlation_id;
      activeRunRef.current = nextRun;
      enqueueingRef.current.delete(turnId);
      // The turn's old blob must not be re-adopted — this version writes a
      // new one, and `hydrate` refuses a message it believes it has already
      // fetched.
      hydratedRef.current.delete(`${chat}:${messageUuid}`);
      setActiveRun(nextRun);

      setTurns((prev) =>
        patchTurn(prev, turnId, (turn): AssistantTurn => ({
          ...turn,
          /**
           * Reconcile the history HERE, not on the next page load.
           *
           * `version_count` comes from `GET /messages`, which nothing
           * refetches after a rerun — so the toggle stayed at one version
           * and appeared only after a reload. The 202 carries everything the
           * toggle needs, and this is the one path all three actions take.
           */
          versions: withNewVersion(turn.versions, accepted, query),
          // The new version is current the moment it is accepted, so the
          // toggle follows the run rather than staying on the old answer.
          activeVersion: accepted.version_number,
          response: undefined,
          summary: '',
          hydration: 'pending',
        }))
      );
      return {
        ok: true,
        versionUuid: accepted.version_uuid,
        versionNumber: accepted.version_number,
        origin: accepted.origin,
      };
    } catch (err: any) {
      const reason = rerunFailureFor(err?.status);
      enqueueingRef.current.delete(turnId);
      // Put the turn back as it was, and say why on the turn itself.
      setTurns((prev) =>
        patchTurn(prev, turnId, (turn): AssistantTurn => ({
          ...turn,
          live: undefined,
          rerunError: RERUN_MESSAGES[reason],
        }))
      );
      return { ok: false, reason };
    }
  }, []);

  const rerun = useCallback((
    messageUuid: string,
    query?: string
  ): Promise<RerunResult> => enqueueVersion(
    messageUuid,
    (chat) => dispatch(researchRerunMessage(chat, messageUuid, query)),
    query
  ), [dispatch, enqueueVersion]);

  const refine = useCallback((
    messageUuid: string,
    instruction?: string,
    fromVersion?: number
  ): Promise<RerunResult> => enqueueVersion(
    messageUuid,
    (chat) => dispatch(researchRefineMessage(chat, messageUuid, instruction, fromVersion))
  ), [dispatch, enqueueVersion]);

  /**
   * Switch which version of an answer is shown.
   *
   * Each version is its own blob, so this is a fetch, not a local swap. A
   * version that cannot be loaded says so — falling back to v1 while the
   * toggle reads v3 would be showing the wrong answer under the right label.
   */
  const selectVersion = useCallback(async (
    messageUuid: string,
    version: number
  ): Promise<void> => {
    const chat = chatUuidRef.current;
    if (!chat || !messageUuid) return;
    const turnId = `${messageUuid}_assistant`;

    setTurns((prev) =>
      patchTurn(prev, turnId, (turn): AssistantTurn => ({
        ...turn,
        activeVersion: version,
        versionLoading: true,
        rerunError: undefined,
      }))
    );

    try {
      const raw = await dispatch(researchGetMessageContent(chat, messageUuid, version));
      const blob = (raw as any)?.content ?? raw;
      const response = toResearchResponse(blob, { messageUuid });
      setTurns((prev) =>
        patchTurn(prev, turnId, (turn): AssistantTurn => ({
          ...turn,
          response,
          intent: response.intent,
          verification: response.meta.verification,
          hydration: 'hydrated',
          versionLoading: false,
          live: undefined,
        }))
      );
    } catch {
      setTurns((prev) =>
        patchTurn(prev, turnId, (turn): AssistantTurn => ({
          ...turn,
          versionLoading: false,
          // No silent fallback to another version.
          response: undefined,
          hydration: 'failed',
          rerunError: 'That version could not be loaded.',
        }))
      );
    }
  }, [dispatch]);

  /**
   * Rate an answer.
   *
   * Optimistic, because a thumb that waits for a round trip feels broken.
   * The previous value is captured first and restored on failure, so a
   * rejected rating cannot leave the UI claiming something the backend never
   * recorded.
   */
  const rate = useCallback(async (
    messageUuid: string,
    rating: ResearchRating
  ): Promise<void> => {
    const chat = chatUuidRef.current;
    if (!chat || !messageUuid) return;
    const turnId = `${messageUuid}_assistant`;

    const turn = turnsRef.current.find((t) => t.id === turnId) as AssistantTurn | undefined;
    /**
     * The rating belongs to the version ON SCREEN.
     *
     * v1 and v2 are different answers; a thumb given to one says nothing
     * about the other, so the rating targets whichever version the reader is
     * actually looking at.
     */
    const version = turn?.activeVersion;
    const before = versionFeedback(turn?.versions, version, turn?.feedback ?? null);

    setTurns((prev) =>
      patchTurn(prev, turnId, (t): AssistantTurn => ({
        ...t,
        feedback: rating,
        versions: typeof version === 'number'
          ? withVersionFeedback(t.versions, version, rating)
          : t.versions,
      }))
    );

    try {
      await dispatch(researchRateMessage(chat, messageUuid, rating, version));
    } catch {
      setTurns((prev) =>
        patchTurn(prev, turnId, (t): AssistantTurn => ({
          ...t,
          feedback: before,
          versions: typeof version === 'number'
            ? withVersionFeedback(t.versions, version, before)
            : t.versions,
        }))
      );
    }
  }, [dispatch]);

  /* ── clarify gate: the question upgrading itself in place ────── */

  /**
   * The ONE outstanding question in this chat, if there is one.
   *
   * The schema permits exactly one live pending plan per chat (the filtered
   * unique index over `awaiting_clarification`/`planned`), so this is a find,
   * not a list — and that is precisely why the upgrade replaces a card rather
   * than stacking a second one.
   */
  const openQuestion = useMemo(() => {
    const turn = turns.find(
      (t): t is AssistantTurn =>
        isAssistantTurn(t) && !!t.clarification && !t.clarification.settled
    );
    return turn ? { turnId: turn.id, pending: turn.clarification!.pending } : null;
  }, [turns]);

  /**
   * `research.clarify` — the authored question replacing the provisional one.
   *
   * IN PLACE, and the round does NOT advance (§3.1): this is the same question
   * said better, not a second one. What the user has already typed or picked
   * survives wherever the question id survives, so an upgrade that lands while
   * they are mid-answer costs them nothing.
   */
  /*
    TWO correlations, because there are two ways a question arrives.

    A question already on screen upgrades on its OWN correlation. But the L2-B
    hold happens to a turn that is RUNNING — the send was accepted, the engine
    got as far as choosing an output format, could not choose it, and held
    before retrieval — and that event comes down the run's correlation. Passing
    only the open question's id meant that when there was no open question
    there was no subscription at all: the event the engine sent was never
    heard, the turn kept running, and the user got an artifact for a turn the
    engine had already stopped. The header of `useClarifyUpgrade` has always
    described this second case; nothing had ever wired it.
  */
  useClarifyUpgrade(
    openQuestion?.pending.correlation_id || activeRun?.correlationId || null,
    (event) => {
      const current = openQuestion;

      // ── The upgrade: a provisional question, said better, in place ──
      if (current && clarifyEventMatches(event, current.pending)) {
        setTurns((prev) =>
          patchTurn(prev, current.turnId, (turn): AssistantTurn => {
            if (!turn.clarification || turn.clarification.settled) return turn;
            const upgraded = applyClarifyUpgrade(turn.clarification.pending, event);
            if (upgraded === turn.clarification.pending) return turn;
            return {
              ...turn,
              awaitingClarification: true,
              clarification: {
                ...turn.clarification,
                pending: upgraded,
                draft: upgradedDraft(turn.clarification.draft, upgraded),
                error: undefined,
              },
            };
          })
        );
        return;
      }
      if (current) return;

      // ── The hold: a running turn, stopped and asked ──
      const running = activeRunRef.current;
      if (!running) return;
      const payload: any = event;
      const correlation = payload.correlation_id || payload.correlationId;
      if (correlation && correlation !== running.correlationId) return;
      const message = payload.message_uuid || payload.messageUuid;
      if (message && running.messageUuid && message !== running.messageUuid) return;

      const pending = pendingFromClarifyEvent(event, {
        messageUuid: running.messageUuid,
        correlationId: running.correlationId,
      });
      // An event with nothing to ask cannot stop a turn. Whatever went wrong
      // upstream, a running turn is better than a card with no question in it.
      if (!pending.questions.length) return;

      /*
        A SECOND HOLD IS A REAL THING, and the turn has to come back out of its
        answered state for it.

        The engine can hold twice on one turn: jurisdiction first, and then —
        once the answer to that has resolved and the turn has resumed — the
        output format. The second event arrives on the same correlation, on a
        turn whose clarification is now `settled`, and refusing it there is
        what froze the live test: no card was rendered, `live` was left on the
        turn from the resume, and the orphan sweep polled `/content` for a
        result that was waiting on a question nobody had been shown.

        What must still be refused is a REDELIVERY of the question already
        answered, which a reconnect will produce. `sameQuestionSet` is the line
        between the two — see its note.
      */
      const existingTurn = turnsRef.current.find(
        (t): t is AssistantTurn =>
          isAssistantTurn(t) && t.id === running.assistantTurnId
      );
      const existing = existingTurn?.clarification;
      if (existing && sameQuestionSet(existing.pending, pending)) return;

      setTurns((prev) =>
        patchTurn(prev, running.assistantTurnId, (turn): AssistantTurn => ({
          ...turn,
          awaitingClarification: true,
          // The engine held BEFORE retrieval, so there is no answer coming
          // and nothing to keep spinning over. Clearing this is also what
          // re-arms the content-poll guard on a turn that had resumed.
          live: undefined,
          clarification: { pending, draft: initialDraft(pending) },
        }))
      );

      /* Close the run subscription. The turn is waiting on the user now, and
         leaving it open spends the run's timeout budget on a question nobody
         has been asked to answer yet. Answering re-opens it on the same
         correlation — see `answerClarificationRef`. */
      setActiveRun(null);
    }
  );

  /* ── clarify gate: answering, and giving up ─────────────────── */

  /**
   * Answer an outstanding question. The SAME turn then continues.
   *
   * Two outcomes and they are genuinely different things on screen:
   *
   *   * RESOLVED — the backend enqueued under the correlation id and message
   *     uuid this turn already had, so the run subscription opens on the turn
   *     that is already in the transcript. The card collapses to the compact
   *     "asked and answered" strip and the spinner takes over IN PLACE. No new
   *     turn appears, because no new turn happened.
   *   * STILL OPEN — round two. The card stays, with the new questions. There
   *     is no round three; the backend runs with stated defaults instead.
   */
  const answerClarificationRef = useRef(
    async (_message: string, _answers: Record<string, string>) => {}
  );
  answerClarificationRef.current = async (messageUuid, answers) => {
    const chat = chatUuidRef.current;
    if (!chat || !messageUuid) return;
    const turnId = `${messageUuid}_assistant`;

    setTurns((prev) =>
      patchTurn(prev, turnId, (turn): AssistantTurn => ({
        ...turn,
        clarification: turn.clarification && {
          ...turn.clarification,
          draft: { ...turn.clarification.draft, ...answers },
          submitting: true,
          error: undefined,
        },
      }))
    );

    try {
      const result: any = await dispatch(
        researchAnswerClarification(chat, messageUuid, answers)
      );

      const outcome = classifyAnswerResult(result);

      if (outcome === 'not_started') {
        // 409, and ONLY 409: the turn genuinely never started. The question
        // stays exactly as it was — answerable, with a reason under it. This
        // is the one branch that may say "did not start", and everything else
        // that used to land here (an `already_running` 202 above all) now does
        // not, because a running turn drawn as a failed one is a lie the user
        // cannot act on.
        setTurns((prev) =>
          patchTurn(prev, turnId, (turn): AssistantTurn => ({
            ...turn,
            clarification: turn.clarification && {
              ...turn.clarification,
              submitting: false,
              error: 'That did not start. Answer again to try it.',
            },
          }))
        );
        return;
      }

      if (outcome === 'question') {
        // Round two: a NEW question set on the same turn.
        const pending = pendingFromSendResult({
          ...result,
          message_uuid: messageUuid,
        });
        // A round-two response with nothing to ask is malformed, and rendering
        // it would replace a live question with an empty card the user can
        // neither answer nor dismiss. Treat it as the failure it is and leave
        // the question they had.
        if (!pending.questions.length) {
          throw new Error('clarify: no questions and no run');
        }
        setTurns((prev) =>
          patchTurn(prev, turnId, (turn): AssistantTurn => ({
            ...turn,
            awaitingClarification: true,
            clarification: { pending, draft: initialDraft(pending) },
          }))
        );
        return;
      }

      // Resolved. The question becomes a record of itself and the turn goes
      // back to being a run — the one it always was.
      setTurns((prev) =>
        patchTurn(prev, turnId, (turn): AssistantTurn => ({
          ...turn,
          awaitingClarification: false,
          clarification: turn.clarification && {
            ...turn.clarification,
            draft: { ...turn.clarification.draft, ...answers },
            submitting: false,
            settled: true,
          },
          live: { stages: [], done: false, error: null },
          // Marked HERE too, not only on send: a clarification answered after
          // a reload resumes a turn that was read back from history, and that
          // answer is every bit as live as one from a fresh question.
          liveArrival: true,
        }))
      );

      // THE SAME TURN, RESUMED. The correlation id came back unchanged, so
      // this is the subscription a fresh send would have opened — not a second
      // one racing the first. On an `already_running` 202 the response names
      // the run that beat this submit, and that is the one carrying events.
      const resumed = resumedCorrelationId(
        result,
        pendingOf(turnsRef.current, turnId)
      );
      activeCorrelationRef.current = resumed;
      setActiveRun({
        correlationId: resumed,
        messageUuid,
        assistantTurnId: turnId,
        chatUuid: chat,
        explicitIntent: null,
      });
    } catch {
      setTurns((prev) =>
        patchTurn(prev, turnId, (turn): AssistantTurn => ({
          ...turn,
          clarification: turn.clarification && {
            ...turn.clarification,
            submitting: false,
            error: 'That did not go through. Please try again.',
          },
        }))
      );
    }
  };
  const answerClarification = useCallback(
    (messageUuid: string, answers: Record<string, string>) =>
      answerClarificationRef.current(messageUuid, answers),
    []
  );

  /**
   * Give up on an unanswered question.
   *
   * NOT `stop()`. There is nothing to stop — nothing was enqueued — and going
   * through the stop path leaves the backend's pending row behind, which
   * resurfaces on the next open as a question about a turn the user believes
   * they cancelled.
   */
  const abandonClarification = useCallback(async (messageUuid: string) => {
    const chat = chatUuidRef.current;
    if (!chat || !messageUuid) return;
    const turnId = `${messageUuid}_assistant`;
    setTurns((prev) =>
      patchTurn(prev, turnId, (turn): AssistantTurn => ({
        ...turn,
        awaitingClarification: false,
        clarification: undefined,
        live: { stages: [], done: true, error: 'Cancelled before it ran.' },
      }))
    );
    try {
      await dispatch(researchAbandonPending(chat, messageUuid));
    } catch {
      // The row may survive; the next open will offer the question again,
      // which is the recoverable side of this failure.
    }
  }, [dispatch]);

  const reset = useCallback(() => {
    activeCorrelationRef.current = null;
    lastKeyRef.current = null;
    openedRef.current = null;
    setTurns([]);
    setTitle('');
    setChatUuid('');
    chatUuidRef.current = '';
    setActiveRun(null);
    setHistoryCursor(null);
    setHasMoreHistory(false);
    hydratedRef.current.clear();
  }, []);
  resetRef.current = reset;

  return {
    turns,
    chatUuid,
    title,
    rename,
    isLoadingHistory,
    isLoadingOlder,
    hasMoreHistory,
    isSending,
    scopeWrites,
    run,
    send,
    stop,
    hydrate,
    rerun,
    refine,
    selectVersion,
    rate,
    answerClarification,
    abandonClarification,
    loadOlder,
    open,
    reset,
    ensureChat,
  };
}

export default useResearchSession;
